import { useMemo } from "react"
import { toast } from "sonner"
import { ShotModelMenu } from "@/components/shot-panel/shot-model-menu"
import { useShotPanelWorkspace } from "@/hooks/use-shot-panel-workspace"
import { useTranslation } from "@/hooks/use-translation"
import { sortedCatalogModelOptions } from "@/lib/generation-model-option"
import { patchShotStoryboardModelKey } from "@/lib/inertia-actions"

/**
 * Модель кадра и раскладки этого шота — параметр пульта генерации. Пусто — модель проекта;
 * своя модель подсвечена. Без прав на правку — просто подпись.
 */
export function ShotStoryboardModelChip({
  projectId,
  shotId,
  modelKey,
  projectModelLabel,
  placement = "below",
  onSaved,
}: {
  projectId: number
  shotId: number
  /** Своя модель шота; null — модель проекта. */
  modelKey: string | null
  projectModelLabel: string
  placement?: "above" | "below"
  onSaved: (next: string | null) => void
}) {
  const { t } = useTranslation(["shot", "common", "workspace"])
  const { project_permissions, generation_models_by_context } = useShotPanelWorkspace()
  const canEdit = Boolean(project_permissions?.can_edit_content)
  // Сервер сохраняет модель кадра шота только из реестра — остальные не предлагаем.
  const models = useMemo(
    () => (generation_models_by_context?.storyboard ?? []).filter((model) => model.registry === true),
    [generation_models_by_context?.storyboard],
  )
  const current = modelKey ? models.find((model) => model.id === modelKey) : null
  const label = current?.label?.trim() || projectModelLabel

  const options = useMemo(() => sortedCatalogModelOptions(models, t("common:platformDisabledModel")), [models, t])

  const handleChange = (next: string | null) => {
    const previous = modelKey
    onSaved(next)
    void patchShotStoryboardModelKey(projectId, shotId, next).catch((err: Error) => {
      onSaved(previous)
      toast.error(err.message || t("common.saveErrorRetry"))
    })
  }

  return (
    <ShotModelMenu
      value={modelKey}
      options={options}
      projectLabel={projectModelLabel}
      label={label}
      hint={t("assetShelf.changeFrameModel")}
      canEdit={canEdit && models.length > 0}
      placement={placement}
      onChange={handleChange}
    />
  )
}
